import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi, type PatientConsent } from '../api';
import type { ConsentGrant } from '../state/patientProductionStore';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import ConsentRequestsInbox from '../components/ConsentRequestsInbox';


/**
 * The scope list is now the SERVER's (`GET /consent-scopes`), because scopes
 * are enforced: each one opens a named section of the doctor's patient view.
 * The six words that used to sit here were this page's own invention and no
 * gate anywhere read them — a patient who ticked "Lab Reports" alone granted
 * exactly as much as one who ticked everything.
 */

const DURATIONS = [
  { hours: 1, label: '1 hour' },
  { hours: 6, label: '6 hours' },
  { hours: 24, label: '24 hours' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '7 days' },
  { hours: 720, label: '30 days' },
];

const statusColor = (status: string) => {
  if (status === 'ACTIVE') return { bg: '#E6F9F0', color: '#0A7B4F' };
  if (status === 'REVOKED') return { bg: '#FDE8E8', color: '#C53030' };
  return { bg: '#FEF3CD', color: '#92710A' };
};

const roleBadgeColor = (role: string) => {
  const map: Record<string, string> = {
    Doctor: '#3B82F6', Lab: '#8B5CF6', Pharmacy: '#10B981',
    Hospital: '#EF4444', Insurance: '#F59E0B', 'Family Member': '#EC4899',
  };
  return map[role] || '#6B7280';
};


export default function ConsentSharing() {
  const navigate = useNavigate();
  const [apiGrants, setApiGrants] = useState<PatientConsent[]>([]);
  const [scopes, setScopes] = useState<{ scope: string; label: string; description: string }[]>([]);
  /** The provider the typed card id resolves to — or why it does not. */
  const [recipient, setRecipient] = useState<{ nhcId: string; name: string; role: string; detail: string | null; verified: boolean } | null>(null);
  const [lookupState, setLookupState] = useState<'idle' | 'looking' | 'missing'>('idle');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [form, setForm] = useState({
    recipientNhcId: '',
    scopes: [] as string[],
    validHours: 24,
  });

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const loadApi = useCallback(() => {
    setLoading(true);
    patientApi.getConsents()
      .then((data) => { if (aliveRef.current) setApiGrants(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => { if (aliveRef.current) setLoading(false); });
  }, []);

  useEffect(() => { loadApi(); }, [loadApi]);

  useEffect(() => {
    patientApi.consentScopes()
      .then((rows) => { if (aliveRef.current) setScopes(rows ?? []); })
      .catch(() => { /* the picker then offers nothing rather than words that gate nothing */ });
  }, []);

  /**
   * Who the typed card id belongs to.
   *
   * A consent binds by card id and nothing else, so the patient has to be able
   * to see WHO they are about to share with before they do. This form used to
   * take a typed name and no id at all, so every grant it created listed as
   * ACTIVE and opened nothing for anybody.
   */
  const lookup = async (nhcId: string) => {
    const id = nhcId.trim();
    setRecipient(null);
    if (!id) { setLookupState('idle'); return; }
    setLookupState('looking');
    try {
      const found = await patientApi.findConsentRecipient(id);
      if (!aliveRef.current) return;
      setRecipient(found);
      setLookupState('idle');
    } catch {
      if (aliveRef.current) setLookupState('missing');
    }
  };

  // Everything on this page is the server's. The device store used to be
  // merged in on top, so a grant the server never recorded could still list as
  // ACTIVE — the one thing a consent screen must never do.
  const grants = [...apiGrants].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const toggleScope = (val: string) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.includes(val) ? f.scopes.filter(s => s !== val) : [...f.scopes, val],
    }));
  };

  const handleCreate = async () => {
    if (!recipient || form.scopes.length === 0) return;
    setCreating(true);
    setActionError(null);
    try {
      await patientApi.createConsent({
        sharedWithName: recipient.name,
        sharedWithRole: recipient.role,
        sharedWithNhcId: recipient.nhcId,
        scopes: form.scopes,
        validHours: form.validHours,
      });
      setForm({ recipientNhcId: '', scopes: [], validHours: 24 });
      setRecipient(null);
      setShowModal(false);
      loadApi();
    } catch {
      // A consent that the server did not record must never show as ACTIVE —
      // keep the modal open and tell the user the grant was NOT created.
      setActionError('Consent could not be created — the server is unreachable. No access was granted; please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Revoke this consent? The recipient will lose access immediately.')) return;
    setActionError(null);
    try {
      await patientApi.revokeConsent(id);
      loadApi();
    } catch {
      // Server still considers this consent ACTIVE — showing REVOKED locally
      // would falsely tell the patient access has been cut off.
      setActionError('Revoke failed — the server could not be reached, so this consent is STILL ACTIVE. Please retry.');
    }
  };

  return (
    <div style={{ ...govPageContainer() }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ ...GOV_TYPE.pageTitle }}>Consent & Sharing</div>
          <div style={{ fontSize: 11, color: T.textTer, marginTop: 2 }}>Control who accesses your health data</div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          style={{
            fontSize: 11, fontWeight: 600, color: '#FFF', background: T.teal || '#0D9488',
            border: 'none', borderRadius: 10, padding: '8px 16px', cursor: 'pointer', fontFamily: T.font,
          }}
        >
          + New Share
        </button>
      </div>

      {actionError && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 12, padding: '10px 14px', marginBottom: 12 }}>
          <span style={{ fontFamily: T.font, fontSize: 11, color: '#B91C1C', fontWeight: 600 }}>{actionError}</span>
        </div>
      )}

      {/* Requests a provider raised, which only the patient can answer */}
      <ConsentRequestsInbox onDecided={loadApi} />

      {/* Info Banner */}
      <div style={{
        background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 16, padding: 14,
        marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E' }}>Consent-Based Sharing</div>
          <div style={{ fontSize: 9, color: '#92400E', marginTop: 2, lineHeight: 1.5 }}>
            You control exactly what data is shared, with whom, and for how long. Every access is logged.
          </div>
        </div>
      </div>

      {/* Access History Link */}
      <div
        onClick={() => navigate('/patient/access-logs')}
        style={{
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: '10px 14px',
          marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', boxShadow: T.shadow,
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.text }}>Access History</div>
          <div style={{ fontSize: 9, color: T.textTer, marginTop: 1 }}>View who has accessed your records</div>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </div>

      {/* Loading */}
      {loading && grants.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', fontSize: 11, color: T.textTer }}>Loading...</div>
      )}

      {/* Empty State */}
      {!loading && grants.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={T.border} strokeWidth="1.5" strokeLinecap="round" style={{ margin: '0 auto 10px' }}>
            <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" />
          </svg>
          <div style={{ fontSize: 11, color: T.textTer, fontWeight: 600 }}>No consent grants yet</div>
          <div style={{ fontSize: 9, color: T.textTer, marginTop: 4 }}>Tap "New Share" to share data with a provider</div>
        </div>
      )}

      {/* Grant Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {grants.map(grant => {
          const sc = statusColor(grant.status);
          const rc = roleBadgeColor(grant.recipientRole);
          const isActive = grant.status === 'ACTIVE';
          return (
            <div key={grant.id} style={{
              background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
              padding: 14, boxShadow: T.shadow, opacity: isActive ? 1 : 0.6,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Name + Badges */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{grant.recipientName}</span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: '#FFF', background: rc,
                      padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap',
                    }}>{grant.recipientRole}</span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: sc.color, background: sc.bg,
                      padding: '2px 8px', borderRadius: 4,
                    }}>{grant.status}</span>
                  </div>
                  {/* Only the grant's own id is shown — the recipient's NHC ID
                      is not known here and must never be synthesized. */}
                  <div style={{ fontSize: 9, color: T.teal, fontFamily: 'monospace', fontWeight: 600, marginBottom: 4 }}>
                    Grant {grant.id.slice(0, 8)}
                  </div>

                  {/* Valid Until */}
                  <div style={{ fontSize: 9, color: T.textTer, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                    </svg>
                    Valid until {new Date(grant.validUntil).toLocaleString('en-IN')}
                  </div>

                  {/* Scope Chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {/* Named by the server, so this page no longer spells a
                        constant out and calls it a label. */}
                    {(grant.scopeLabels.length ? grant.scopeLabels : grant.scopes).map((label: string) => (
                      <span key={label} style={{
                        fontSize: 9, fontWeight: 600, color: T.teal || '#0D9488',
                        background: '#F0FDFA', padding: '2px 8px', borderRadius: 4,
                      }}>
                        {label}
                      </span>
                    ))}
                  </div>

                  {/* A grant that binds to nobody is still listed — the patient
                      created it and deserves to know why it does nothing. */}
                  {!grant.effective && (
                    <div style={{
                      marginTop: 6, fontSize: 9.5, fontWeight: 600, color: '#B45309',
                      background: '#FFFBEB', border: '1px solid #FDE68A',
                      borderRadius: 6, padding: '4px 8px',
                    }}>
                      This grant has no NeoHealthCard id to bind to, so nobody can act on it.
                      Grant it again to the recipient&rsquo;s card id.
                    </div>
                  )}
                </div>

                {/* Actions */}
                {isActive && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginLeft: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => {
                        const text = `NeoHealthCard Consent Grant\nRecipient: ${grant.recipientName} (${grant.recipientRole})\nScopes: ${(grant.scopeLabels.length ? grant.scopeLabels : grant.scopes).join(', ')}\nValid until: ${new Date(grant.validUntil).toLocaleString('en-IN')}\nGrant ID: ${grant.id}`;
                        navigator.clipboard.writeText(text).catch(() => {});
                      }}
                      style={{
                        background: `${T.teal}10`, border: 'none', borderRadius: 8,
                        padding: '6px 10px', cursor: 'pointer', fontSize: 9,
                        fontWeight: 700, color: T.teal, fontFamily: T.font,
                      }}
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => handleRevoke(grant.id)}
                      style={{
                        background: '#FEF2F2', border: 'none', borderRadius: 8,
                        padding: '6px 10px', cursor: 'pointer', fontSize: 9,
                        fontWeight: 700, color: '#DC2626', fontFamily: T.font,
                      }}
                    >
                      Revoke
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ ...GOV_COMPLIANCE.text, marginTop: 12 }}>
        NeoHealthCard™ · Consent-based sharing · Every access is audit logged
      </div>

      {/* New Share Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{
            background: T.white, borderRadius: 16, width: '100%', maxWidth: 420,
            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 16px', borderBottom: `1px solid ${T.border}`,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.font }}>Create New Share</span>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
                  color: T.textTer, padding: 4, lineHeight: 1,
                }}
              >
                &#x2715;
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Who — by their card id, which is what a grant binds to.
                  This used to be a typed name and a role dropdown, so every
                  grant created here listed as ACTIVE and bound to nobody. */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font, display: 'block', marginBottom: 4 }}>
                  Recipient&rsquo;s NeoHealthCard ID *
                </label>
                <input
                  value={form.recipientNhcId}
                  onChange={e => { setForm(f => ({ ...f, recipientNhcId: e.target.value })); setRecipient(null); setLookupState('idle'); }}
                  onBlur={e => lookup(e.target.value)}
                  placeholder="NHC-D-2026-80272"
                  style={{
                    width: '100%', boxSizing: 'border-box', border: `1px solid ${lookupState === 'missing' ? '#FCA5A5' : T.border}`,
                    borderRadius: 10, padding: '8px 12px', fontSize: 11, fontFamily: 'monospace',
                    color: T.text, outline: 'none', background: T.white,
                  }}
                />
                {lookupState === 'looking' && (
                  <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 5, fontFamily: T.font }}>Looking them up…</div>
                )}
                {lookupState === 'missing' && (
                  <div style={{ fontSize: 10.5, color: '#B91C1C', marginTop: 5, fontFamily: T.font, lineHeight: 1.5 }}>
                    No provider on NeoHealthCard carries that id. Check it with them — a grant can only
                    be given to somebody this platform can match.
                  </div>
                )}
                {recipient && (
                  <div style={{
                    marginTop: 8, padding: '8px 10px', borderRadius: 10,
                    background: '#F0FDFA', border: '1px solid #99F6E4', fontFamily: T.font,
                  }}>
                    <div style={{ fontSize: 11.5, fontWeight: 800, color: T.text }}>{recipient.name}</div>
                    <div style={{ fontSize: 10, color: T.textSec, marginTop: 1 }}>
                      {[recipient.role, recipient.detail].filter(Boolean).join(' · ')}
                      {/* Said either way — an unverified provider can still be
                          shared with, and the patient should know which. */}
                      {recipient.verified ? ' · Verified' : ' · Not yet verified'}
                    </div>
                  </div>
                )}
              </div>

              {/* Data Scopes */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font, display: 'block', marginBottom: 6 }}>
                  Data Scopes *
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {scopes.map(sc => {
                    const s = { value: sc.scope, label: sc.label };
                    const checked = form.scopes.includes(s.value);
                    return (
                      <label
                        key={s.value}
                        onClick={() => toggleScope(s.value)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px',
                          borderRadius: 8, cursor: 'pointer', background: checked ? '#F0FDFA' : 'transparent',
                          border: checked ? '1px solid #99F6E4' : '1px solid transparent',
                        }}
                      >
                        <div style={{
                          width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                          border: checked ? 'none' : `1.5px solid ${T.border}`,
                          background: checked ? (T.teal || '#0D9488') : T.white,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {checked && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font }}>{s.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Valid For */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font, display: 'block', marginBottom: 4 }}>
                  Valid For
                </label>
                <select
                  value={form.validHours}
                  onChange={e => setForm(f => ({ ...f, validHours: Number(e.target.value) }))}
                  style={{
                    width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`,
                    borderRadius: 10, padding: '8px 12px', fontSize: 11, fontFamily: T.font,
                    color: T.text, outline: 'none', background: T.white, appearance: 'auto' as any,
                  }}
                >
                  {DURATIONS.map(d => <option key={d.hours} value={d.hours}>{d.label}</option>)}
                </select>
              </div>
            </div>

            {/* Modal Footer */}
            {actionError && (
              <div style={{ margin: '0 16px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '8px 12px' }}>
                <span style={{ fontFamily: T.font, fontSize: 10.5, color: '#B91C1C', fontWeight: 600 }}>{actionError}</span>
              </div>
            )}
            <div style={{
              display: 'flex', gap: 10, padding: '12px 16px',
              borderTop: `1px solid ${T.border}`,
            }}>
              <button
                onClick={() => { setShowModal(false); setActionError(null); }}
                style={{
                  flex: 1, padding: '10px 0', fontSize: 11, fontWeight: 700, fontFamily: T.font,
                  color: T.textTer, background: T.white, border: `1px solid ${T.border}`,
                  borderRadius: 10, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                // Only enabled once a REAL recipient has been resolved — a
                // grant to an unmatched id would bind to nobody.
                disabled={creating || !recipient || form.scopes.length === 0}
                style={{
                  flex: 1, padding: '10px 0', fontSize: 11, fontWeight: 700, fontFamily: T.font,
                  color: '#FFF', background: (!recipient || form.scopes.length === 0) ? '#A0AEC0' : (T.teal || '#0D9488'),
                  border: 'none', borderRadius: 10, cursor: creating ? 'wait' : 'pointer',
                  opacity: creating ? 0.7 : 1,
                }}
              >
                {creating ? 'Creating...' : 'Create Share'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
